import React, { useState, useEffect } from 'react';
import type { Vocabulary, CreateVocabularyDTO, VocabularyGroup } from '../../types/vocabulary.types';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { ErrorMessage } from '../common/ErrorMessage';
import { dictionaryService } from '../../services/dictionary.service';
import { playWordPronunciation } from '../../utils/audio';

interface VocabularyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateVocabularyDTO) => Promise<void>;
  initialData?: Vocabulary | null;
  availableGroups?: VocabularyGroup[];
}

export function VocabularyFormModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  availableGroups = [],
}: VocabularyFormModalProps): React.ReactElement | null {
  const isEditing = Boolean(initialData);

  const [word, setWord] = useState('');
  const [meaningVi, setMeaningVi] = useState('');
  const [ipa, setIpa] = useState('');
  const [exampleSentence, setExampleSentence] = useState('');
  const [synonyms, setSynonyms] = useState<string[]>([]);
  const [collocations, setCollocations] = useState<string[]>([]);
  const [audioUrl, setAudioUrl] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  const [newSynonym, setNewSynonym] = useState('');
  const [newCollocation, setNewCollocation] = useState('');

  const [fieldErrors, setFieldErrors] = useState<{ word?: string; meaningVi?: string }>({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Dictionary lookup state ───────────────────────────────
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  useEffect(() => {
    if (initialData) {
      setWord(initialData.word);
      setMeaningVi(initialData.meaningVi);
      setIpa(initialData.ipa ?? '');
      setExampleSentence(initialData.exampleSentence ?? '');
      setSynonyms(initialData.synonyms ?? []);
      setCollocations(initialData.collocations ?? []);
      setAudioUrl(initialData.audioUrl ?? '');
      setSelectedGroupIds(initialData.groups?.map((g) => g.id) ?? []);
    } else {
      setWord('');
      setMeaningVi('');
      setIpa('');
      setExampleSentence('');
      setSynonyms([]);
      setCollocations([]);
      setAudioUrl('');
      setSelectedGroupIds([]);
    }
    setFieldErrors({});
    setApiError('');
    setLookupError('');
    setNewSynonym('');
    setNewCollocation('');
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  // ── Dictionary lookup ─────────────────────────────────────
  const handleLookup = async (): Promise<void> => {
    const trimmedWord = word.trim();
    if (!trimmedWord) {
      setLookupError('Please enter a word before looking it up.');
      return;
    }
    if (trimmedWord.length > 100) {
      setLookupError('Word must not exceed 100 characters.');
      return;
    }

    setIsLookingUp(true);
    setLookupError('');

    try {
      const result = await dictionaryService.lookupWord(trimmedWord);

      // Populate form fields with lookup result.
      // User can edit any of these before saving.
      if (result.meaningVi) {
        setMeaningVi(result.meaningVi);
        // Clear validation error on meaningVi if it was set
        setFieldErrors((prev) => ({ ...prev, meaningVi: undefined }));
      }
      if (result.ipa) {
        setIpa(result.ipa);
      }
      if (result.exampleSentence) {
        setExampleSentence(result.exampleSentence);
      }
      if (result.audioUrl) {
        setAudioUrl(result.audioUrl);
      }
      if (result.synonyms.length > 0) {
        // Merge with existing synonyms, deduplicate
        setSynonyms((prev) => {
          const merged = [...prev];
          for (const s of result.synonyms) {
            if (!merged.some((m) => m.toLowerCase() === s.toLowerCase())) {
              merged.push(s);
            }
          }
          return merged;
        });
      }
      if (result.collocations.length > 0) {
        setCollocations((prev) => {
          const merged = [...prev];
          for (const c of result.collocations) {
            if (!merged.some((m) => m.toLowerCase() === c.toLowerCase())) {
              merged.push(c);
            }
          }
          return merged;
        });
      }
    } catch (err) {
      setLookupError(
        err instanceof Error ? err.message : 'Dictionary lookup failed. Please try again.',
      );
    } finally {
      setIsLookingUp(false);
    }
  };

  // ── Tag helpers ───────────────────────────────────────────

  const handleAddSynonym = (e?: React.FormEvent): void => {
    if (e) e.preventDefault();
    const trimmed = newSynonym.trim();
    if (trimmed && !synonyms.includes(trimmed)) {
      setSynonyms([...synonyms, trimmed]);
      setNewSynonym('');
    }
  };

  const handleRemoveSynonym = (index: number): void => {
    setSynonyms(synonyms.filter((_, idx) => idx !== index));
  };

  const handleAddCollocation = (e?: React.FormEvent): void => {
    if (e) e.preventDefault();
    const trimmed = newCollocation.trim();
    if (trimmed && !collocations.includes(trimmed)) {
      setCollocations([...collocations, trimmed]);
      setNewCollocation('');
    }
  };

  const handleRemoveCollocation = (index: number): void => {
    setCollocations(collocations.filter((_, idx) => idx !== index));
  };

  // ── Submit ────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setApiError('');

    const errors: { word?: string; meaningVi?: string } = {};
    if (!word.trim()) errors.word = 'Word is required.';
    if (!meaningVi.trim()) errors.meaningVi = 'Vietnamese meaning is required.';

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        word: word.trim(),
        meaningVi: meaningVi.trim(),
        ipa: ipa.trim() || undefined,
        audioUrl: audioUrl.trim() || undefined,
        synonyms: synonyms.length > 0 ? synonyms : undefined,
        collocations: collocations.length > 0 ? collocations : undefined,
        exampleSentence: exampleSentence.trim() || undefined,
        groupIds: selectedGroupIds,
      });
      onClose();
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to save vocabulary.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusy = isSubmitting || isLookingUp;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="relative w-full max-w-lg my-8 glass-card border-white/10 p-6 rounded-2xl shadow-2xl bg-surface-950/95 border text-slate-100">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-bold text-white">
              {isEditing ? 'Edit Vocabulary' : 'Add New Vocabulary'}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {isEditing
                ? 'Update your vocabulary details and examples.'
                : 'Add a new IELTS word to your personal learning collection.'}
            </p>
          </div>
          <button
            id="close-vocab-modal-btn"
            onClick={onClose}
            disabled={isBusy}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* API Error Message */}
        {apiError && (
          <div className="mt-4">
            <ErrorMessage message={apiError} />
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-4 space-y-4" noValidate>

          {/* Word + Tra cứu */}
          <div className="space-y-1.5">
            <label htmlFor="vocab-word-input" className="text-sm font-medium text-slate-300">
              Word <span className="text-red-400">*</span>
            </label>
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <input
                  id="vocab-word-input"
                  type="text"
                  placeholder="e.g. environment"
                  value={word}
                  onChange={(e) => {
                    setWord(e.target.value);
                    if (fieldErrors.word) setFieldErrors((p) => ({ ...p, word: undefined }));
                    // Clear lookup error when user changes word
                    if (lookupError) setLookupError('');
                  }}
                  disabled={isBusy}
                  required
                  className={`w-full bg-white/5 border rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 transition-all ${
                    fieldErrors.word
                      ? 'border-red-500/60 focus:ring-red-500/40'
                      : 'border-white/10 focus:ring-primary-500/40 focus:border-primary-500/40'
                  }`}
                />
                {fieldErrors.word && (
                  <p className="mt-1 text-xs text-red-400">{fieldErrors.word}</p>
                )}
              </div>
              <button
                id="vocab-lookup-btn"
                type="button"
                onClick={() => void handleLookup()}
                disabled={!word.trim() || isBusy}
                className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border
                  bg-indigo-500/20 border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/30 hover:text-indigo-200
                  disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-indigo-500/20"
              >
                {isLookingUp ? (
                  <>
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>Đang tra…</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>Tra cứu</span>
                  </>
                )}
              </button>
              {word.trim() && (
                <button
                  id="vocab-listen-btn"
                  type="button"
                  onClick={() => {
                    playWordPronunciation(
                      word,
                      audioUrl,
                      () => setIsPlayingAudio(true),
                      () => setIsPlayingAudio(false),
                    );
                  }}
                  title={`Pronounce "${word}"`}
                  aria-label={`Listen to ${word}`}
                  className={`flex-shrink-0 p-2.5 rounded-xl text-sm transition-all border ${
                    isPlayingAudio
                      ? 'bg-primary-500/30 border-primary-500/50 text-primary-200 ring-1 ring-primary-400/40'
                      : 'bg-white/5 border-white/10 text-slate-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <svg className={`w-4 h-4 ${isPlayingAudio ? 'animate-pulse text-primary-400' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                </button>
              )}
            </div>

            {/* Lookup error — shown below word row */}
            {lookupError && (
              <p className="text-xs text-amber-400 flex items-center gap-1.5 mt-1">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {lookupError}
              </p>
            )}
          </div>

          {/* IPA */}
          <Input
            id="vocab-ipa-input"
            label="IPA Pronunciation"
            placeholder="e.g. /ɪnˈvaɪrənmənt/"
            value={ipa}
            onChange={(e) => setIpa(e.target.value)}
            disabled={isBusy}
          />

          {/* Vietnamese Meaning */}
          <Input
            id="vocab-meaning-input"
            label="Vietnamese Meaning *"
            placeholder="e.g. môi trường, sinh thái"
            value={meaningVi}
            onChange={(e) => {
              setMeaningVi(e.target.value);
              if (fieldErrors.meaningVi) setFieldErrors((p) => ({ ...p, meaningVi: undefined }));
            }}
            error={fieldErrors.meaningVi}
            disabled={isBusy}
            required
          />

          {/* Synonyms Tag Input */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-300">Synonyms</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {synonyms.map((syn, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-primary-500/15 text-primary-300 border border-primary-500/30"
                >
                  {syn}
                  <button
                    type="button"
                    onClick={() => handleRemoveSynonym(idx)}
                    disabled={isBusy}
                    className="hover:text-white transition-colors disabled:opacity-50"
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                id="vocab-synonym-input"
                type="text"
                placeholder="Type synonym and press Add or Enter"
                value={newSynonym}
                onChange={(e) => setNewSynonym(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSynonym();
                  }
                }}
                disabled={isBusy}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <Button
                type="button"
                id="vocab-add-synonym-btn"
                variant="secondary"
                size="sm"
                onClick={() => handleAddSynonym()}
                disabled={!newSynonym.trim() || isBusy}
              >
                + Add
              </Button>
            </div>
          </div>

          {/* Collocations Tag Input */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-300">Collocations</label>
              <span className="text-xs text-slate-500">(suggestions from lookup — please review)</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {collocations.map((col, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                >
                  {col}
                  <button
                    type="button"
                    onClick={() => handleRemoveCollocation(idx)}
                    disabled={isBusy}
                    className="hover:text-white transition-colors disabled:opacity-50"
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                id="vocab-collocation-input"
                type="text"
                placeholder="Type collocation and press Add or Enter"
                value={newCollocation}
                onChange={(e) => setNewCollocation(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCollocation();
                  }
                }}
                disabled={isBusy}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <Button
                type="button"
                id="vocab-add-collocation-btn"
                variant="secondary"
                size="sm"
                onClick={() => handleAddCollocation()}
                disabled={!newCollocation.trim() || isBusy}
              >
                + Add
              </Button>
            </div>
          </div>

          {/* Example Sentence */}
          <div className="space-y-1.5">
            <label htmlFor="vocab-example-input" className="text-sm font-medium text-slate-300">
              Example Sentence
            </label>
            <textarea
              id="vocab-example-input"
              rows={2}
              placeholder="e.g. We must take collective action to protect the environment."
              value={exampleSentence}
              onChange={(e) => setExampleSentence(e.target.value)}
              disabled={isBusy}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Groups / Collections Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-300 flex items-center gap-1.5">
                <svg className="w-4 h-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span>Collections & Groups</span>
              </label>
              <span className="text-xs text-slate-400 font-medium">
                {selectedGroupIds.length === 0 ? 'No group assigned' : `${selectedGroupIds.length} selected`}
              </span>
            </div>
            {availableGroups.length === 0 ? (
              <p className="text-xs text-slate-500 italic bg-white/5 border border-white/5 rounded-xl p-2.5">
                No groups created yet. You can create groups from the My Vocabulary page.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5 p-2 rounded-xl bg-white/5 border border-white/10 max-h-32 overflow-y-auto">
                {availableGroups.map((group) => {
                  const isChecked = selectedGroupIds.includes(group.id);
                  return (
                    <button
                      key={group.id}
                      type="button"
                      id={`vocab-group-toggle-${group.id}`}
                      onClick={() => {
                        setSelectedGroupIds((prev) =>
                          isChecked ? prev.filter((id) => id !== group.id) : [...prev, group.id],
                        );
                      }}
                      disabled={isBusy}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        isChecked
                          ? 'bg-indigo-500/25 text-indigo-200 border border-indigo-500/50 shadow-sm'
                          : 'bg-white/5 hover:bg-white/10 text-slate-400 border border-white/5'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${isChecked ? 'bg-indigo-400 ring-2 ring-indigo-400/30' : 'bg-slate-500'}`} />
                      <span>{group.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>


          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              id="vocab-form-submit-btn"
              type="submit"
              variant="primary"
              loading={isSubmitting}
              disabled={isBusy}
            >
              {isEditing ? 'Save Changes' : 'Create Vocabulary'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
